/**
 * Affected-assets section splice (ENG-5383).
 *
 * WHY THIS EXISTS
 * ---------------
 * Customers want the finding's description to name the asset(s) the finding
 * actually landed on, between the **Impact** and **Recommendation** sections.
 * That text cannot live in the markdown templates: definitions are stored
 * name-keyed at `definitions/<Risk.Name>` and are shared by every finding of
 * that type across every tenant, so the affected asset simply is not known
 * when the writeup is authored. The section therefore has to be spliced into
 * the description at render time, once, for every template shape.
 *
 * `stripDefinitionMetadata` (VulnerabilityDrawer.tsx) is the existing
 * precedent for a central, template-agnostic markdown transform applied right
 * before render; this splice sits immediately after it.
 *
 * WHY THE HEADING LEVEL IS NOT FIXED
 * ----------------------------------
 * Producers disagree on heading depth, so the emitted heading mirrors the
 * anchor heading's level rather than hard-coding one:
 *   - PlexTrac writeups emit `## Impact` / `## Recommendation`
 *     (`backend/pkg/tabularium/model/risk.go`, `renderRiskDefinition`).
 *   - AI/agent per-instance definitions emit `#### Impact` / `#### Recommendation`
 *     (`backend/pkg/tasks/capabilities/constantine/report.go`).
 *   - Both `Recommendation` and `Recommendations` are recognized spellings
 *     (`backend/pkg/tasks/capabilities/export/report_section_order.go`).
 *
 * WHY FENCED CODE BLOCKS ARE SKIPPED
 * ----------------------------------
 * A `## Recommendation` inside a ``` / ~~~ fence is sample content, not a
 * section heading. Anchoring on it would splice the asset list into the middle
 * of a code sample. The same backend file named above skips heading matches
 * inside fences via its `inFence` helper; this mirrors that behavior.
 *
 * WHY A TRUNCATION DISCLOSURE EXISTS
 * ----------------------------------
 * The caller's asset list comes from `useQueryAssets`, which is paginated and
 * NOT auto-paginated: its main leg requests a bounded page (`limit`, default
 * 100) and its risk-scoped leg takes the backend default page size. The drawer
 * deliberately does not walk `fetchNextPage` to exhaustion — a large roll-up
 * finding would mean hundreds of sequential requests inside a drawer render.
 * So for a finding with more assets than one page, the labels handed to this
 * function are a prefix of the real set. Silently rendering that prefix as if
 * it were the whole set is the actual defect; the caller therefore passes
 * `options.truncated` (derived from the hook's existing `hasNextPage`) and this
 * module discloses the shortfall in the rendered section. The complete,
 * properly paginated list lives in the drawer's dedicated **Assets** tab, which
 * is where the disclosure points the reader.
 *
 * SCOPE: drawer-only. The export/report pipeline and PlexTrac sync render the
 * stored definition directly and do not carry this section.
 *
 * @module vulnerabilities/utils/affectedAssetsSection
 */

/** Heading text emitted for the injected section. */
export const AFFECTED_ASSETS_HEADING = 'Assets Affected';

/** Heading levels that count as real section anchors (`##`..`####`). */
const MIN_ANCHOR_LEVEL = 2;
const MAX_ANCHOR_LEVEL = 4;

/** Level used when the document has no level-2..4 heading to imitate. */
const DEFAULT_HEADING_LEVEL = 2;

/** Opens a fenced code block: up to 3 leading spaces, then ``` or ~~~ or longer. */
const FENCE_OPEN_RE = /^ {0,3}(`{3,}|~{3,})/;

/** ATX heading: up to 3 leading spaces, 1-6 hashes, then at least one space/tab. */
const HEADING_RE = /^ {0,3}(#{1,6})[ \t]+(.*)$/;

/** Characters that could turn a hostname/path into markup if left raw. */
const MARKDOWN_ESCAPE_RE = /[\\`*_[\]<>|#~]/g;

interface HeadingRef {
  /** Index into the split line array. */
  line: number;
  /** Number of leading hashes (1-6). */
  level: number;
  /** Normalized, lowercased heading text used for matching. */
  text: string;
}

/**
 * Normalize a heading's raw text for matching: drop a closing-hash run
 * (`## Impact ##`), surrounding `*`/`_` emphasis, and a trailing colon; then
 * trim and lowercase.
 */
function normalizeHeadingText(raw: string): string {
  let text = raw.trim();
  // Closing ATX hash run, e.g. `Impact ##`. CommonMark requires whitespace
  // before the run, so `C#` keeps its hash.
  text = text.replace(/\s#+$/, '').trim();
  // Surrounding emphasis markers, e.g. `**Impact**` or `_Impact_`.
  text = text
    .replace(/^[*_]+/, '')
    .replace(/[*_]+$/, '')
    .trim();
  // Trailing colon, e.g. `Impact:`.
  text = text.replace(/:+$/, '').trim();
  return text.toLowerCase();
}

/**
 * Index every ATX heading that is NOT inside a fenced code block.
 *
 * Fence rules follow CommonMark closely enough for this purpose: a fence opens
 * on ``` / ~~~ (3+ of the same character, up to 3 leading spaces) and closes on
 * a later line whose marker uses the SAME character and is at least as long,
 * with nothing else on the line. An unterminated fence swallows the rest of the
 * document.
 */
function indexHeadings(lines: string[]): HeadingRef[] {
  const headings: HeadingRef[] = [];
  let fenceChar: string | null = null;
  let fenceLength = 0;

  for (const [line, raw] of lines.entries()) {
    if (fenceChar !== null) {
      // The closing run must be HOMOGENEOUS: a run of backticks OR a run of
      // tildes, never a mix. A `[`~]+` character class matches a MIXED run, so
      // three backticks followed by a tilde would satisfy both the
      // first-character and the length check below and wrongly close a backtick
      // fence (and the mirror closes a tilde fence).
      const close = raw.match(/^ {0,3}(`+|~+)[ \t]*$/);
      if (
        close &&
        close[1][0] === fenceChar &&
        close[1].length >= fenceLength
      ) {
        fenceChar = null;
        fenceLength = 0;
      }
      continue;
    }

    const open = raw.match(FENCE_OPEN_RE);
    if (open) {
      fenceChar = open[1][0];
      fenceLength = open[1].length;
      continue;
    }

    const heading = raw.match(HEADING_RE);
    if (heading) {
      headings.push({
        line,
        level: heading[1].length,
        text: normalizeHeadingText(heading[2]),
      });
    }
  }

  return headings;
}

function isAnchorLevel(level: number): boolean {
  return level >= MIN_ANCHOR_LEVEL && level <= MAX_ANCHOR_LEVEL;
}

/**
 * Escape a display label so a hostname, path, or attacker-influenced
 * identifier cannot render as emphasis, a link, inline code, or raw HTML.
 * Newlines and tabs collapse to a single space so one label stays one bullet.
 */
function escapeMarkdownLabel(label: string): string {
  return label
    .replace(/[\r\n\t]+/g, ' ')
    .trim()
    .replace(MARKDOWN_ESCAPE_RE, match => `\\${match}`);
}

/**
 * Splice an "Assets Affected" section into a finding description.
 *
 * Anchor resolution, in order:
 *   (a) before the first level-2..4 `Recommendation` / `Recommendations`
 *       heading, emitted at that heading's level;
 *   (b) else at the end of the level-2..4 `Impact` section (before the next
 *       heading of the same or higher rank, or at end of document), emitted at
 *       the Impact heading's level;
 *   (c) else appended at the end, at the document's first level-2..4 heading
 *       level, defaulting to `##`.
 *
 * Returns `source` unchanged when there are no usable labels, when `source` is
 * blank, or when the document already carries an "Assets Affected" heading at
 * any heading level (1-6) — a definition author may have written one by hand,
 * possibly at a level this function would never anchor on, and it must never be
 * duplicated. Those short-circuits run before anything below, so
 * `options.truncated` can never produce a disclosure line without a list above
 * it.
 *
 * @param source Markdown description body (already metadata-stripped).
 * @param assetLabels Resolved display labels, in the order they should render.
 * @param options.truncated `true` when the caller's asset query has further
 *   unfetched pages, i.e. `assetLabels` is only a prefix of the finding's real
 *   asset set. Appends a disclosure line naming the count actually rendered and
 *   pointing at the drawer's **Assets** tab (see the module header). Defaults to
 *   `false`, which emits output byte-identical to omitting the argument.
 */
export function injectAffectedAssetsSection(
  source: string,
  assetLabels: string[],
  options?: { truncated?: boolean }
): string {
  const labels = assetLabels.filter(label => label && label.trim().length > 0);
  if (labels.length === 0) return source;
  if (!source || source.trim().length === 0) return source;

  const eol = source.includes('\r\n') ? '\r\n' : '\n';
  const endedWithNewline = /\r?\n$/.test(source);
  const lines = source.split(/\r?\n/);

  const headings = indexHeadings(lines);

  // Idempotence: an author-written section wins; never emit a second one.
  // Deliberately NOT level-restricted — anchor resolution below only trusts
  // levels 2..4, but an author who wrote `# Assets Affected` or
  // `##### Assets Affected` still already has the section, and splicing a
  // second one in is the defect. So this matches any heading level 1..6.
  const normalizedTarget = AFFECTED_ASSETS_HEADING.toLowerCase();
  if (headings.some(h => h.text === normalizedTarget)) {
    return source;
  }

  const anchors = headings.filter(h => isAnchorLevel(h.level));

  let insertAt: number;
  let level: number;

  const recommendation = anchors.find(
    h => h.text === 'recommendation' || h.text === 'recommendations'
  );
  const impact = anchors.find(h => h.text === 'impact');

  if (recommendation) {
    // (a) Immediately before the Recommendation heading.
    insertAt = recommendation.line;
    level = recommendation.level;
  } else if (impact) {
    // (b) At the end of the Impact section — before the next heading of the
    // same or higher rank (a lower `level` number is a higher rank).
    level = impact.level;
    const next = headings.find(
      h => h.line > impact.line && h.level <= impact.level
    );
    insertAt = next ? next.line : lines.length;
  } else {
    // (c) Appended at the end, imitating the document's first real heading.
    level = anchors[0]?.level ?? DEFAULT_HEADING_LEVEL;
    insertAt = lines.length;
  }

  const appending = insertAt >= lines.length;
  if (appending) {
    // Don't leave a pile of blank lines between the old body and the section.
    while (lines.length > 0 && lines[lines.length - 1].trim().length === 0) {
      lines.pop();
    }
    insertAt = lines.length;
  }

  const block: string[] = [];
  if (insertAt > 0 && lines[insertAt - 1].trim().length > 0) {
    block.push('');
  }
  block.push(`${'#'.repeat(level)} ${AFFECTED_ASSETS_HEADING}`, '');
  for (const label of labels) {
    block.push(`- ${escapeMarkdownLabel(label)}`);
  }
  if (options?.truncated) {
    // The leading '' is load-bearing: without a blank line the disclosure is a
    // LAZY CONTINUATION of the last bullet under CommonMark and renders as
    // italic text glued onto it. The blank line closes the list so the
    // (non-indented) disclosure becomes its own paragraph.
    // Count the bullets actually emitted, not `assetLabels.length` — the
    // whitespace filter above may have dropped some of the caller's entries.
    block.push(
      '',
      `_Showing the first ${labels.length} ${labels.length === 1 ? 'asset' : 'assets'}. See the **Assets** tab for the complete list._`
    );
  }
  if (appending) {
    // Restore the document's original trailing newline, if it had one.
    if (endedWithNewline) block.push('');
  } else if (lines[insertAt].trim().length > 0) {
    block.push('');
  }

  lines.splice(insertAt, 0, ...block);
  return lines.join(eol);
}
